import { useMemo, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserFilterApiService } from '@/api/userProfile/UserFilterApiService';
import { UserFilterListItem } from '@/models/user/userFilter';
import { savedFiltersEvents } from './savedFiltersEvents';

const MAX_RECENT_FILTERS = 3;
const SAVED_FILTERS_QUERY_KEY = ['saved-filters'];

interface UseSavedFiltersResult {
  filters: UserFilterListItem[];
  recentFilters: UserFilterListItem[];
  isLoading: boolean;
  hasFilters: boolean;
  refetch: () => void;
}

const fetchSavedFilters = async (): Promise<UserFilterListItem[]> => {
  const response = await UserFilterApiService.getFilters();
  return (response.data.filters ?? [])
    .filter(userFilter => userFilter.filterType === 'saved_filter')
    .sort((a, b) => Date.parse(b.filterDate) - Date.parse(a.filterDate));
};

/** Fetches saved filters via TanStack Query with automatic cache invalidation. */
const useSavedFilters = (): UseSavedFiltersResult => {
  const queryClient = useQueryClient();

  const { data: filters = [], isLoading } = useQuery({
    queryKey: SAVED_FILTERS_QUERY_KEY,
    queryFn: fetchSavedFilters,
    staleTime: 0,
    retry: false,
  });

  const refetch = useCallback(
    () => { queryClient.invalidateQueries({ queryKey: SAVED_FILTERS_QUERY_KEY }); },
    [queryClient],
  );

  useEffect(() => savedFiltersEvents.subscribe(refetch), [refetch]);

  const recentFilters = useMemo(() => filters.slice(0, MAX_RECENT_FILTERS), [filters]);

  return { filters, recentFilters, isLoading, hasFilters: filters.length > 0, refetch };
};

export default useSavedFilters;
